/**
 * Content API client for fetching stories from content-api.arkturian.com
 */

const API_BASE = 'https://content-api.arkturian.com/api/v1'

export interface StoryListItem {
  id: number
  story_group: string
  language: string
  title: string
  subtitle: string
  narrator: string
  color: string
  cover: string
  region: string
  duration: string
  scene_count: number
  available_languages: string[]
}

export interface StoryListResponse {
  stories: StoryListItem[]
  total: number
}

export interface MediaItem {
  id: number
  storage_id: number
  position: number
  caption: string | null
  role: string | null
  media_url: string
}

export interface PostResponse {
  id: number
  title: string
  subtitle?: string | null
  slug?: string | null
  doc_type?: string | null
  content_type: string
  content: string | null
  status: string
  tags?: string[] | null
  metadata_json: Record<string, unknown> | null
  media: MediaItem[]
}

export async function fetchStories(language?: string): Promise<StoryListResponse> {
  const hole = async (lang?: string): Promise<StoryListResponse> => {
    const params = lang ? `?language=${lang}` : ''
    const res = await fetch(`${API_BASE}/stories/${params}`)
    if (!res.ok) throw new Error(`Stories fetch failed: ${res.status}`)
    return res.json()
  }
  const liste = await hole(language)
  if (!language || language === 'de') return liste

  /* Die Liste fuehrt NUR Sagen, die es in der gefragten Sprache gibt —
   * gemessen am 2026-08-11: de 10, en 7, sl 0. Ein slowenischer Besucher
   * saehe also eine leere Geschichten-Seite, obwohl zehn Sagen dastehen.
   *
   * Deshalb wird die deutsche Liste ergaenzt: die uebersetzten Sagen in der
   * Sprache des Besuchers, die uebrigen deutsch. Welche das sind, sieht er
   * im Leser — der zeigt den Hinweis, sobald die gelieferte Sprache von der
   * gewaehlten abweicht. Dieselbe Regel wie ueberall sonst: ein vorhandener
   * Text ist besser als eine leere Zeile. */
  try {
    const deutsch = await hole('de')
    const vorhanden = new Set((liste.stories ?? []).map(s => s.story_group))
    const ergaenzt = (deutsch.stories ?? []).filter(s => !vorhanden.has(s.story_group))
    if (ergaenzt.length === 0) return liste
    return { ...liste, stories: [...(liste.stories ?? []), ...ergaenzt] }
  } catch {
    return liste
  }
}

export async function fetchPost(postId: number): Promise<PostResponse> {
  const res = await fetch(`${API_BASE}/posts/${postId}`)
  if (!res.ok) throw new Error(`Post fetch failed: ${res.status}`)
  return res.json()
}

export interface StoryScene {
  id: string
  title: string
  narrative: string
  mood: string
  location?: string | null
  illustrationPrompt?: string | null
  illustrationId?: number | null
  videoId?: number | null
  audioId?: number | null
}

export interface StoryDetail {
  id: number
  story_group: string
  language: string
  title: string
  subtitle: string
  narrator: string
  color: string
  cover: string
  region: string
  duration: string
  scenes: StoryScene[]
  available_languages: string[]
}

export async function fetchStory(storyGroup: string, language?: string): Promise<StoryDetail> {
  // `auto_generate=false` ist NICHT optional, auch wenn es wie eine
  // Vorsichtsmassnahme aussieht: der Endpunkt hat den Parameter mit Vorgabe
  // `true`, und damit loest JEDE Leseanfrage auf eine fehlende Sprachfassung
  // eine kostenpflichtige LLM-Uebersetzung aus (Content 2026-08-11, geprueft
  // an der laufenden Fassung: stories.py:512). Ein GET, das Geld ausgibt.
  // Dass bisher nichts kostete, lag allein daran, dass der Anbieter
  // erschoepft war — der Ausfall hat den Fehler ueberhaupt sichtbar gemacht.
  // Content stellt die Vorgabe um; bis dahin (und danach als Absicherung)
  // sagen wir es ausdruecklich.
  const hole = async (lang?: string) => {
    const params = lang
      ? `?language=${lang}&auto_generate=false`
      : '?auto_generate=false'
    return fetch(`${API_BASE}/stories/${storyGroup}${params}`)
  }
  let res = await hole(language)
  // Fehlt eine Sprachfassung, antwortet content-api mit 500 — nicht mit 404
  // und ohne Rueckfall (geprueft 2026-08-11: `sage-waechter?language=en` und
  // jede Geschichte auf `sl`). Ein englischsprachiger Leser bekaeme dann statt
  // der deutschen Sage einen Serverfehler. Deshalb hier EIN zweiter Versuch
  // ohne Sprache; die Ursache gehoert trotzdem in den Dienst.
  if (!res.ok && language && language !== 'de') {
    res = await hole(undefined)
  }
  if (!res.ok) throw new Error(`Story fetch failed: ${res.status}`)
  return res.json()
}

/**
 * Narration persona available for a track — one entry per character the
 * user can pick as audio-guide narrator. Served by content-api which owns
 * the persona posts (SSOT for voice_id + avatar_color, Federation Post #1280).
 * `voice_id === null` means the character has text but no cloned voice yet:
 * selectable in the picker, audio playback shows a "voice coming" state.
 */
export interface TrackPersona {
  persona_id: string
  name: string
  persona_post_id: number
  voice_id: string | null
  voice_source: string
  voice_model: string
  avatar_color: string
  available_languages: string[]
  /** language → narration post id (e.g. {"de": 447, "sl": 1271}) */
  post_ids: Record<string, number>
  default_language: string
}

export interface TrackPersonasResponse {
  track_id: string
  personas: TrackPersona[]
}

export async function fetchTrackPersonas(trackId: number): Promise<TrackPersonasResponse> {
  const res = await fetch(`${API_BASE}/tracks/${trackId}/personas`)
  if (!res.ok) throw new Error(`Track personas fetch failed: ${res.status}`)
  return res.json()
}
